import type Luminary from '@lumen5/luminary';
import type { MediaSourceData, SafeLuminary } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type { BrandKit, ImageAssetModel } from 'js/src/client';
import { SlideBackgroundModelTypeEnum } from 'js/src/client';
import { getBrandKitById, loadBrandKits } from 'js/src/data/brand-kits';
import {
    setTemplateColorShuffleIndex,
    setTemplateColorShufflePalettes,
} from 'js/src/data/color-shuffle';
import { ensureFontsLoaded, getFontList } from 'js/src/data/fonts';
import { checkAssetUUID } from 'js/src/data/media';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import { markBrandKitApplied } from 'js/src/data/sketch-controls/sketch-controls-slice';
import { setBrandKitId } from 'js/src/data/video';
import { checkpointVideo } from 'js/src/data/video-data';
import { colorSwapperByLuminance } from 'js/src/libs/color-utils';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';
import uuid from 'js/src/libs/uuid';
import { saveRawLuminary } from 'js/src/luminary/index';
import {
    createLuminaryOperation,
    type LuminaryOperationContext,
} from 'js/src/luminary/luminary-hooks';
import { toMutableCopy } from 'js/src/libs/consts';

function createWatermarkSourceData(details: ImageAssetModel): MediaSourceData {
    return {
        uuid: checkAssetUUID(details.uuid),
        type: SlideBackgroundModelTypeEnum.IMAGE,
        thumbnail_url: details.thumbnail_url,
        preview_url: details.preview_url,
        render_url: details.render_url,
        source: details.source,
        source_id: details.source_id,
        license: details.license,
        is_editorial: details.is_editorial ?? false,
        width: details.width,
        height: details.height,
        title: details.title,
    };
}

/**
 * Applies brand kit colors, fonts, and watermarks directly to a luminary instance's template.
 * This is a pure utility with no Redux side effects, usable in any context
 * (scene previews, video creation, etc.)
 */
export function applyBrandKitToLuminary(
    luminaryInstance: SafeLuminary,
    brandKitData: BrandKit,
    fontList: ReturnType<typeof getFontList>,
) {
    // Prepare data before batching to avoid unnecessary work inside batchRun
    const currentColors = luminaryInstance.template.colors;
    const isDuplicationAllowed = currentColors.length > brandKitData.colors.length;
    const appendUnusedNewColors = !isDuplicationAllowed;
    const matchedColors = colorSwapperByLuminance(
        currentColors,
        brandKitData.colors,
        isDuplicationAllowed,
        appendUnusedNewColors,
    );

    const fontORM = new PlainJsStoreORM(fontList);
    const brandKitFontUrls: string[] = [];
    brandKitData.fonts.forEach((brandKitFontId) => {
        const font = fontORM.getById(brandKitFontId);
        if (font) {
            brandKitFontUrls.push(font.url);
        }
    });

    // Batch all template mutations so Luminary fires a single state change event
    // instead of one per modification. Without this, brand kits with many colors,
    // fonts, and watermarks cause the Creator to hang due to repeated re-renders.
    luminaryInstance.batchRun((safeLuminary) => {
        // 1. Apply colors
        safeLuminary.template.colors = matchedColors;

        // 2. Apply fonts
        if (brandKitFontUrls.length > 0) {
            const numFonts = safeLuminary.template.fonts?.length || 0;
            const maxFonts = Math.max(numFonts, brandKitFontUrls.length);
            const fontsToApply: string[] = [];
            for (let i = 0; i < maxFonts; i++) {
                fontsToApply.push(
                    brandKitFontUrls[i] || brandKitFontUrls[brandKitFontUrls.length - 1],
                );
            }
            safeLuminary.template.fonts = fontsToApply;
        }

        // 3. Apply watermarks
        if (brandKitData.watermark && brandKitData.watermarkDetails) {
            const watermarkDetails = Array.isArray(brandKitData.watermarkDetails)
                ? brandKitData.watermarkDetails
                : [brandKitData.watermarkDetails];

            const numExisting = safeLuminary.template.watermarkSources.length;

            // Replace existing watermark slots in-place
            for (let i = 0; i < numExisting; i++) {
                const detailIndex = Math.min(i, watermarkDetails.length - 1);
                const details = watermarkDetails[detailIndex];
                if (details) {
                    safeLuminary.template.setWatermarkSourceAtIndex(
                        { sourceData: createWatermarkSourceData(details) },
                        i,
                    );
                }
            }

            // Add new watermark slots beyond existing template slots
            for (let i = numExisting; i < watermarkDetails.length; i++) {
                const details = watermarkDetails[i];
                if (details) {
                    safeLuminary.template.addWatermarkSource({
                        sourceData: createWatermarkSourceData(details),
                    });
                }
            }

            // Select the first watermark source and make it visible
            safeLuminary.template.watermark.sourceData =
                VideoData.templateWatermarkSourcesByIndex(0);
            safeLuminary.template.watermark.visible = true;
        } else {
            safeLuminary.template.watermark.sourceData = VideoData.WATERMARK_NONE;
        }
    });
}

/**
 * Applies a brand kit to a standalone Luminary instance (one not managed by
 * the editor's Luminary context), loading the brand kit list and font list
 * into Redux first if needed. Brand kit fonts are stored as font IDs that can
 * only be resolved to URLs through the font list, so applying a brand kit
 * before fonts are loaded silently drops its fonts.
 */
export function loadAndApplyBrandKitToLuminary(luminaryInstance: Luminary, brandKitId: number) {
    return async (dispatch: AppDispatch, getState: GetRootState): Promise<void> => {
        await dispatch(ensureFontsLoaded());

        let brandKit = getBrandKitById(getState(), brandKitId);
        if (!brandKit) {
            await dispatch(loadBrandKits());
            brandKit = getBrandKitById(getState(), brandKitId);
        }
        if (!brandKit) {
            return;
        }

        applyBrandKitToLuminary(
            luminaryInstance,
            toMutableCopy<BrandKit>(brandKit),
            getFontList(getState()),
        );
        dispatch(markBrandKitApplied());
    };
}

export type ApplyBrandKitOptions = {
    systemTriggered?: boolean;
    parentActionGroup?: string;
    skipAnalytics?: boolean;
};

/**
 * Applies a brand kit to the current video
 *
 * This operation:
 * 1. Applies the brand kit's color palette using luminance-based matching
 * 2. Applies the brand kit's fonts to the template
 * 3. Replaces watermarks in-place (1→1, 2→2, etc.) to preserve scene references
 *    - If template has more slots than brand kit watermarks, duplicates the last one
 *    - If brand kit has more watermarks than template slots, adds new ones
 * 4. Updates the Redux state with the selected brand kit ID
 *
 * @param brandKitId - The ID of the brand kit to apply
 * @param options - Optional configuration for the operation
 */
export const applyBrandKitOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState }: LuminaryOperationContext<Luminary>,
        brandKitId: number,
        options: ApplyBrandKitOptions = {},
    ) => {
        const {
            systemTriggered = false,
            parentActionGroup = uuid(),
            skipAnalytics = false,
        } = options;

        const state = getState();

        // Get the brand kit from Redux
        const brandKit = getBrandKitById(state, brandKitId);
        if (!brandKit) {
            // Brand kit was deleted — clear the stale reference instead of crashing
            dispatch(
                setBrandKitId({
                    brandKitId: null,
                    systemTriggered,
                    parentActionGroup,
                }),
            );
            return;
        }

        const brandKitJS = toMutableCopy<BrandKit>(brandKit);

        if (!skipAnalytics) {
            analytics.track('Brand kit selected');
        }

        // Apply colors, fonts, and watermarks to the template
        const fontList = getFontList(state);
        applyBrandKitToLuminary(luminary, brandKitJS, fontList);

        // Checkpoint all changes
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        // 4. Clear cached color shuffle palettes so they are regenerated from the
        //    new brand kit colors on the next shuffle click
        dispatch(
            setTemplateColorShufflePalettes({
                palettes: [],
                systemTriggered,
                parentActionGroup,
            }),
        );
        dispatch(
            setTemplateColorShuffleIndex({
                colorShuffleIndex: 0,
                systemTriggered,
                parentActionGroup,
            }),
        );

        // 5. Update Redux state with the selected brand kit ID
        dispatch(
            setBrandKitId({
                brandKitId: brandKitJS.id,
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);
